/**
 * Self-serve signup page.
 *
 * Behaviour driven by the platform's payment mode:
 *   • DEMO mode  → submit → org+admin provisioned + auto-login → /portal
 *   • LIVE mode  → submit → backend creates Zoho Payments session → widget opens →
 *                  on success, /signup/verify provisions org+admin and auto-logs in.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Rocket, CheckCircle, Lock } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function loadZohoPaymentsScript() {
    return new Promise((resolve) => {
        if (window.ZPayments) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://static.zohocdn.com/zpay/zpay-js/v1/zpayments.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

export default function SignupPage() {
    const [form, setForm] = useState({ org_name: "", name: "", email: "", password: "" });
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pricing, setPricing] = useState(null);
    const [step, setStep] = useState("form"); // "form" | "otp"
    const [otp, setOtp] = useState("");
    const [resendCooldown, setResendCooldown] = useState(0);
    const navigate = useNavigate();
    const { refreshMe } = useAuth();

    useEffect(() => {
        axios.get(`${BASE}/api/public/pricing`).then(({ data }) => setPricing(data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);

    const submit = async (e) => {
        e.preventDefault();
        if (!agreedToTerms) {
            toast.error("Please accept the Terms & Conditions and Privacy Policy to continue.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${BASE}/api/auth/signup/start`, form);
            toast.success(`We sent a 6-digit code to ${form.email}`);
            setStep("otp");
            setResendCooldown(30);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Signup failed");
        } finally { setSubmitting(false); }
    };

    const resendOtp = async () => {
        try {
            await axios.post(`${BASE}/api/auth/signup/start`, form);
            toast.success("New verification code sent");
            setResendCooldown(30);
            setOtp("");
        } catch (err) { toast.error(err?.response?.data?.detail || "Could not resend code"); }
    };

    const verifyOtp = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${BASE}/api/auth/signup/verify-otp`, {
                email: form.email, otp: otp.trim(),
            }, { withCredentials: true });
            if (data.mode === "demo") {
                toast.success(`Welcome aboard, ${data.user.name}! Your workspace is live.`);
                await refreshMe?.();
                navigate("/portal");
                return;
            }
            // LIVE mode → still need payment after OTP. Re-call /signup to open Zoho widget.
            const ok = await loadZohoPaymentsScript();
            if (!ok) { toast.error("Could not load Zoho Payments. Try again."); return; }
            const { data: pay } = await axios.post(`${BASE}/api/auth/signup`, form, { withCredentials: true });
            const instance = new window.ZPayments({
                account_id: pay.zoho_account_id,
                domain: "IN",
                otherOptions: { api_key: pay.zoho_api_key },
            });
            try {
                const result = await instance.requestPaymentMethod({
                    amount: String(pay.amount_inr),
                    transaction_type: "payment",
                    currency_code: "INR",
                    payments_session_id: pay.payment_session_id,
                    currency_symbol: "₹",
                    business: "FormFlo",
                    description: `Activate ${form.org_name}`,
                    invoice_number: pay.signup_id.slice(0, 24),
                    address: { name: form.name, email: form.email },
                });
                const { data: post } = await axios.post(`${BASE}/api/auth/signup/verify`, {
                    signup_id: pay.signup_id,
                    payment_id: result.payment_id,
                    payment_session_id: pay.payment_session_id,
                    signature: result.signature || null,
                }, { withCredentials: true });
                toast.success(`Payment received — welcome, ${post.user.name}!`);
                await refreshMe?.();
                navigate("/portal");
            } catch (err) {
                if (err?.code === "widget_closed") toast("Payment cancelled — you can retry anytime");
                else toast.error(err?.response?.data?.detail || err?.message || "Payment verification failed");
            } finally {
                try { await instance.close(); }
                catch (e) { console.warn("[signup] zoho instance close failed:", e); }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed");
        } finally { setSubmitting(false); }
    };

    const goBackToForm = () => {
        setStep("form");
        setOtp("");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[1fr_540px] auth-grid-bg">
            <div className="hidden lg:flex flex-col justify-between p-12">
                <Link to="/" className="font-display text-2xl font-bold tracking-tight">FormFlo<span className="text-primary">.</span></Link>
                <div className="max-w-md">
                    <div className="label-overline mb-3">Start in minutes</div>
                    <h1 className="font-display text-5xl font-bold tracking-tight mb-5">Spin up your workspace.</h1>
                    <p className="text-muted-foreground leading-relaxed">
                        FormFlo provisions your org, your first admin account, and a base subscription —
                        all in a single step. You can add expansion packs anytime from <strong>Billing</strong>.
                    </p>
                    <ul className="mt-8 space-y-3 text-sm" data-testid="signup-pack-summary">
                        <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-primary" /> {pricing?.users_per_pack ?? 5} users · {pricing?.forms_per_pack ?? 15} forms · {(pricing?.records_per_pack ?? 10000).toLocaleString("en-IN")} records · {pricing?.storage_gb_per_pack ?? 1} GB</li>
                        <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-primary" /> Approvals + RBAC + public form sharing</li>
                        <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-primary" /> Cancel or expand anytime</li>
                    </ul>
                </div>
                <div className="text-xs font-mono text-muted-foreground">FormFlo v1 · self-serve onboarding</div>
            </div>
            <div className="flex items-center justify-center p-6 bg-card">
                <div className="w-full max-w-md">
                    <div className="label-overline mb-2">{step === "otp" ? "Verify your email" : "Create your workspace"}</div>
                    <h2 className="text-3xl font-bold tracking-tight mb-1">{step === "otp" ? "Enter the 6-digit code" : "Create your workspace"}</h2>
                    <p className="text-sm text-muted-foreground mb-3">{step === "otp"
                        ? <>We sent a code to <span className="font-medium text-foreground">{form.email}</span>. It expires in 10 minutes.</>
                        : "Annual subscription · billed via Zoho Payments. No taxes added."}
                    </p>
                    <div className="lg:hidden mb-8 border border-border rounded-sm bg-secondary/40 px-3 py-2 text-xs" data-testid="signup-pack-summary-mobile">
                        <span className="font-mono text-muted-foreground">Pack includes </span>
                        <span className="font-semibold">{pricing?.users_per_pack ?? 5} users · {pricing?.forms_per_pack ?? 15} forms · {(pricing?.records_per_pack ?? 10000).toLocaleString("en-IN")} records · {pricing?.storage_gb_per_pack ?? 1} GB</span>
                    </div>
                    {step === "otp" ? (
                    <form onSubmit={verifyOtp} className="space-y-4" data-testid="signup-otp-form">
                        <div>
                            <Label className="label-overline">Verification code</Label>
                            <Input
                                autoFocus required
                                inputMode="numeric"
                                pattern="\d{6}"
                                maxLength={6}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                className="mt-2 rounded-sm h-14 text-center text-2xl tracking-[0.5em] font-mono"
                                placeholder="••••••"
                                data-testid="signup-otp-input"
                            />
                        </div>
                        <Button type="submit" disabled={submitting || otp.length < 6} className="w-full rounded-sm h-12 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="signup-verify-otp-btn">
                            {submitting ? "Verifying…" : "Verify & continue"}
                        </Button>
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
                            <button type="button" onClick={goBackToForm} className="hover:text-foreground underline-offset-2 hover:underline" data-testid="signup-otp-back">
                                ← Edit details
                            </button>
                            <button type="button" disabled={resendCooldown > 0} onClick={resendOtp} className="hover:text-foreground disabled:opacity-50 underline-offset-2 hover:underline" data-testid="signup-otp-resend">
                                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                            </button>
                        </div>
                    </form>
                    ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
                        <div>
                            <Label className="label-overline">Organization name</Label>
                            <Input required value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} className="mt-2 rounded-sm h-11" placeholder="Acme Inc." data-testid="signup-org-name-input" />
                        </div>
                        <div>
                            <Label className="label-overline">Your name</Label>
                            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm h-11" data-testid="signup-name-input" />
                        </div>
                        <div>
                            <Label className="label-overline">Work email</Label>
                            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-sm h-11" data-testid="signup-email-input" />
                        </div>
                        <div>
                            <Label className="label-overline">Password</Label>
                            <Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 rounded-sm h-11" placeholder="6+ characters" data-testid="signup-password-input" />
                        </div>
                        <label className="flex items-start gap-2 pt-1 text-xs text-muted-foreground cursor-pointer" data-testid="signup-terms-label">
                            <input
                                type="checkbox"
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                className="mt-0.5 accent-primary"
                                data-testid="signup-terms-checkbox"
                                required
                            />
                            <span>
                                I have read and agree to the{" "}
                                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="signup-terms-link">Terms &amp; Conditions</Link>,{" "}
                                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="signup-privacy-link">Privacy Policy</Link>{" "}
                                and{" "}
                                <Link to="/refund" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="signup-refund-link">Refund Policy</Link>.
                                I understand that sensitive data (passwords, card details, government IDs) must not be stored on this platform.
                            </span>
                        </label>
                        <Button type="submit" disabled={submitting || !agreedToTerms} className="w-full rounded-sm h-12 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="signup-submit-btn">
                            {submitting ? "Working…" : <><Rocket size={16} className="mr-2" /> Start workspace</>}
                        </Button>
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-2">
                            <Lock size={11} /> Payments handled by Zoho Payments. We never store your card.
                        </p>
                    </form>
                    )}
                    <div className="mt-8 pt-6 border-t border-border">
                        <p className="text-sm text-muted-foreground">
                            Already have a workspace?{" "}
                            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="signin-link">Sign in</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
